import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { majorApi } from "@/api/major";

export interface Major {
    id: string;
    code: string;
    name: string;
    description?: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface MajorFilters {
    search?: string;
    is_active?: boolean;
    page?: number;
    per_page?: number;
}

export const useMajorStore = defineStore("major", () => {
    // State
    const majors = ref<Major[]>([]);
    const currentMajor = ref<Major | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const isActive = ref<boolean | null>(null);
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Getters
    const activeMajors = computed(() =>
        majors.value.filter((major) => major.is_active),
    );

    // Actions
    const fetchMajors = async (filters: MajorFilters = {}) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await majorApi.getAll({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                is_active: isActive.value !== null ? isActive.value : undefined,
                ...filters,
            });
            majors.value = response.data?.items || response.data || [];
            const metaData = response.data?.meta || null;
            if (metaData) {
                meta.value = metaData;
                page.value = metaData.current_page;
                perPage.value = metaData.per_page;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch majors";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchMajorById = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await majorApi.getById(id);
            currentMajor.value = response;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch major";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createMajor = async (data: any) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await majorApi.create(data);
            await fetchMajors(); // Refresh the list
            return response;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create major";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateMajor = async (id: string, data: any) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await majorApi.update(id, data);
            await fetchMajors(); // Refresh the list
            return response;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update major";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteMajor = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await majorApi.delete(id);
            await fetchMajors(); // Refresh the list
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete major";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchActiveMajors = async () => {
        loading.value = true;
        error.value = null;

        try {
            const response = await majorApi.getActive();
            majors.value = response; // Store the active majors in the state
            return response;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch active majors";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        page.value = newPage;
        fetchMajors();
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1; // Reset to first page
        fetchMajors();
    };

    const resetFilters = () => {
        search.value = "";
        isActive.value = null;
        page.value = 1;
        fetchMajors();
    };

    return {
        // State
        majors,
        currentMajor,
        loading,
        error,
        page,
        perPage,
        search,
        isActive,
        meta,

        // Getters
        activeMajors,

        // Actions
        fetchMajors,
        fetchMajorById,
        createMajor,
        updateMajor,
        deleteMajor,
        fetchActiveMajors,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
